import { Link as RouterLink, useLoaderData, useNavigate } from "react-router";

import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { DateCalendar } from "@mui/x-date-pickers";
import { PickerDay, pickerDayClasses, type PickerDayProps } from "@mui/x-date-pickers/PickerDay";

import { restrictToScrollAreaWithPadding } from "_shared/_views/drag-and-drop/dnd-kit-modifiers";
import AHDate from "_shared/dates/date";
import {
  type CalendarEventRange,
  localCalendarMonthRange,
  useGetCalendarEventsQuery,
} from "calendar/_api/calendar-event-api";
import { calendarRoute, calendarRouteForDate } from "calendar/calendar-navigation";
import { formatEventRange, getSafeExternalLink } from "calendar/calendar-event-display";
import type { CalendarEventResponse } from "calendar/_schemas/calendar-event-schema";
import {
  calendarEventColors,
  calendarEventKindLabels,
  getCalendarEventKind,
  groupCalendarEventsByDate,
  type CalendarEventKind,
} from "calendar/calendar-event";
import {
  dashboardCalendarEmphasisColors,
  getDashboardCalendarDayPresentation,
  getDashboardEventDotPresentation,
  selectDashboardCalendarEvent,
} from "calendar/_views/dashboard-calendar-presentation";
import { useSession } from "users/__session/session-context";
import CtaBackground from "_shared/_views/assets/call-to-action-background.jpg";
import {
  DashboardCardEyebrow,
  DashboardCardGrid,
  DashboardCardHeader,
  DashboardCardState,
  DashboardCardTitle,
  DashboardExternalRowLink,
  DashboardPanelCard,
  DashboardRow,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import ActiveWorkCard from "_shared/_views/dashboards/active-work-card";
import DashboardCardsMenu from "_shared/_views/dashboards/dashboard-cards-menu";
import DraggableDashboardCard from "_shared/_views/dashboards/draggable-dashboard-card";
import BuildYourTeamBanner from "legal-entities/invitations/bulk/_views/build-your-team-banner";
import RecentActivityCard from "_shared/_views/dashboards/recent-activity-card";
import UnreadConversationsCard from "_shared/_views/dashboards/unread-conversations-card";
import WaitingOnYouCard from "_shared/_views/dashboards/waiting-on-you-card";
import {
  dashboardCardIds,
  defaultDashboardCardOrder,
  isDashboardCustomised,
  moveDashboardCard,
  readDashboardCardOrder,
  readHiddenCards,
  reorderDashboardCards,
  toggleHiddenCard,
  writeDashboardCardOrder,
  writeHiddenCards,
  type DashboardCardId,
} from "_shared/_views/dashboards/dashboard-card-order";
import type { Moment } from "moment";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * The page owns its scrolling, as every other page in the shell does: `PageContent` in `main.tsx`
 * is `overflow: hidden`, so whatever a page does not make reachable itself is simply cut off with
 * no scrollbar. The dashboard never did, and at a short viewport — 1920x1080 at 150% display
 * scaling is 1280x720 — the upcoming-events card ran past the fold with no way to reach the rest.
 */
const Main = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1),
  },
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  height: "100%",
  overflowY: "auto",
  padding: theme.spacing(2),
}));

/**
 * The marketplace, as a line rather than a photograph.
 *
 * It used to be a 320px image tile holding a whole card slot on a page whose other cards each
 * carry live data about this user, and the destination is already two clicks away in the side nav
 * — the nav item and the marketplace logo under it. A banner keeps the invitation and gives the
 * slot back (#5076).
 */
const MarketplaceBanner = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== "backgroundImageUrl",
})<{ backgroundImageUrl: string }>(({ backgroundImageUrl, theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2.5),
  borderRadius: theme.spacing(2),
  backgroundImage: `linear-gradient(90deg, rgba(11, 31, 57, 0.94) 45%, rgba(11, 31, 57, 0.55)), url(${backgroundImageUrl})`,
  backgroundPosition: "center 40%",
  backgroundSize: "cover",
  color: theme.palette.common.white,
  textDecoration: "none",
  transition: theme.transitions.create(["filter"]),
  "&:hover": {
    filter: "saturate(1.08)",
  },
}));
const EventDateMonth = styled(Typography)({
  fontWeight: 700,
  lineHeight: 1,
});
const EventDateDay = styled(Typography)({
  lineHeight: 1.15,
});
const GreetingRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
});
const Greeting = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
  },
  margin: 0,
}));
const Calendar = styled(DateCalendar)({
  margin: "auto",
  // Fit the card's padded content area so the picker's fixed width cannot shift its center.
  width: "100%",
  maxWidth: 320,
  minWidth: 0,
});
const CalendarLoadingState = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const calendarDaySize = 36;
const calendarEventDotSize = 6;

type CalendarDayFrameProps = {
  shadeEntireDay: boolean;
};

const CalendarDayFrame = styled("div", {
  shouldForwardProp: (prop) => prop !== "shadeEntireDay",
})<CalendarDayFrameProps>(({ shadeEntireDay }) => ({
  position: "relative",
  width: calendarDaySize,
  height: calendarDaySize,
  borderRadius: 4,
  backgroundColor: shadeEntireDay ? dashboardCalendarEmphasisColors.todayWithEvents : undefined,
}));

const CalendarPickerDay = styled(PickerDay, {
  shouldForwardProp: (prop) => prop !== "shadeEntireDay",
})<{ shadeEntireDay: boolean }>(({ shadeEntireDay, theme }) => shadeEntireDay ? ({
  borderRadius: 4,
  backgroundColor: dashboardCalendarEmphasisColors.todayWithEvents,
  color: theme.palette.text.primary,
  [`&.${pickerDayClasses.today}`]: { border: 0 },
  [`&.${pickerDayClasses.selected}`]: {
    backgroundColor: dashboardCalendarEmphasisColors.todayWithEvents,
    color: theme.palette.text.primary,
  },
  [`&.${pickerDayClasses.selected}:hover`]: {
    backgroundColor: dashboardCalendarEmphasisColors.todayWithEventsHover,
  },
  "&:hover": { backgroundColor: dashboardCalendarEmphasisColors.todayWithEventsHover },
}) : {});

const CalendarEventDots = styled("div")(() => ({
  position: "absolute",
  left: "50%",
  bottom: 1,
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: 1,
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 3,
}));

const CalendarEventDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "eventKind",
})<{ eventKind: CalendarEventKind }>(({ eventKind }) => ({
  width: calendarEventDotSize,
  height: calendarEventDotSize,
  borderRadius: "50%",
  backgroundColor: calendarEventColors[eventKind],
}));

const CalendarEventOverflow = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  transform: "translateY(-1px)",
}));

const CalendarTooltipContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

type DashboardCalendarContextValue = {
  eventsByDate: Map<string, CalendarEventResponse[]>;
  openCalendar: (date: string, event?: CalendarEventResponse) => void;
};

const DashboardCalendarContext = createContext<DashboardCalendarContextValue | undefined>(undefined);

const dashboardUpcomingEventLimit = 3;
const dashboardUpcomingRangeDays = 365;

/** What each card is called, so a drag handle can say which card it picks up. */
const dashboardCardLabels: Record<DashboardCardId, string> = {
  queue: "Waiting on you",
  conversations: "Unread messages",
  work: "Active work",
  "upcoming-events": "Upcoming events",
  "calendar-month": "This month",
  activity: "Recently",
};

export default function Dashboard() {
  const partOfDay = useLoaderData();
  const session = useSession();
  const navigate = useNavigate();
  const [upcomingWindow] = useState(createUpcomingEventWindow);
  const [cardOrder, setCardOrder] = useState(() => readDashboardCardOrder(globalThis.localStorage));
  const [hiddenCards, setHiddenCards] = useState(() => readHiddenCards(globalThis.localStorage));
  // A dedicated handle rather than the whole card, so the links and the calendar inside a card keep
  // working; the sensors only ever see the handle's own pointer events. The touch delay is what
  // lets a finger scroll the page past a handle instead of picking the card up.
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const [visibleMonth, setVisibleMonth] = useState(() => new AHDate().asMoment.clone().startOf("month"));
  const calendarRange = useMemo(
    () => localCalendarMonthRange(visibleMonth.year(), visibleMonth.month()),
    [visibleMonth],
  );
  const eventsQuery = useGetCalendarEventsQuery(calendarRange);
  const upcomingEventsQuery = useGetCalendarEventsQuery(upcomingWindow.range);
  const upcomingEvents = useMemo(
    () => selectUpcomingEvents(upcomingEventsQuery.data ?? [], upcomingWindow.referenceDate),
    [upcomingEventsQuery.data, upcomingWindow.referenceDate],
  );
  const visibleUpcomingEvents = upcomingEvents.slice(0, dashboardUpcomingEventLimit);
  const additionalUpcomingEventCount = Math.max(0, upcomingEvents.length - dashboardUpcomingEventLimit);
  const eventsByDate = useMemo(() => groupCalendarEventsByDate(eventsQuery.data ?? []), [eventsQuery.data]);
  const dashboardCalendarContext = useMemo<DashboardCalendarContextValue>(() => ({
    eventsByDate,
    openCalendar: (date, event) => navigate(calendarRouteForDate(
      date,
      event?.id,
      event ? {
        start: String(event.start),
        end: event.end === null ? null : String(event.end),
        isAllDay: event.isAllDay,
      } : undefined,
    )),
  }), [eventsByDate, navigate]);

  const rememberOrder = (next: DashboardCardId[]) => {
    setCardOrder(next);
    writeDashboardCardOrder(globalThis.localStorage, next);
  };
  const moveCard = (cardId: DashboardCardId, toIndex: number) =>
    rememberOrder(moveDashboardCard(cardOrder, cardId, toIndex));
  const dropCard = (event: DragEndEvent) =>
    rememberOrder(reorderDashboardCards(cardOrder, event.active.id, event.over?.id));

  const rememberHidden = (next: DashboardCardId[]) => {
    setHiddenCards(next);
    writeHiddenCards(globalThis.localStorage, next);
  };
  const toggleCard = (cardId: DashboardCardId) => rememberHidden(toggleHiddenCard(hiddenCards, cardId));
  const resetCards = () => {
    rememberOrder(defaultDashboardCardOrder());
    rememberHidden([]);
  };
  // Only the cards the reader has kept, in the order they put them in.
  const shownCards = cardOrder.filter((cardId) => !hiddenCards.includes(cardId));

  const cardsById: Record<DashboardCardId, ReactNode> = {
    queue: <WaitingOnYouCard />,
    conversations: <UnreadConversationsCard />,
    work: <ActiveWorkCard />,
    "upcoming-events": (
      <UpcomingEventsCard
        isLoading={upcomingEventsQuery.isLoading}
        isError={upcomingEventsQuery.isError}
        events={visibleUpcomingEvents}
        additionalCount={additionalUpcomingEventCount}
      />
    ),
    "calendar-month": (
      <CalendarMonthCard
        calendarContext={dashboardCalendarContext}
        isLoading={eventsQuery.isLoading}
        isError={eventsQuery.isError}
        onMonthChange={(month) => setVisibleMonth(month.clone().startOf("month"))}
      />
    ),
    activity: <RecentActivityCard />,
  };

  return (
    <Main>
      <GreetingRow>
        <Greeting variant="h5">
          {`${partOfDay}, ${session.user?.name.firstName}`}
        </Greeting>
        <DashboardCardsMenu
          cards={dashboardCardIds.map((cardId) => ({ id: cardId, label: dashboardCardLabels[cardId] }))}
          hidden={hiddenCards}
          isCustomised={isDashboardCustomised(cardOrder, hiddenCards)}
          onToggle={toggleCard}
          onReset={resetCards}
        />
      </GreetingRow>
      {/* Above the marketplace invitation, and above the cards: the one thing on this page that
          somebody is waiting on this reader for. It draws nothing at all unless it applies. */}
      <BuildYourTeamBanner />
      <MarketplaceBanner to="/marketplace" backgroundImageUrl={CtaBackground} aria-label="Explore the marketplace">
        <Box>
          <DashboardCardEyebrow>
            <StorefrontRoundedIcon fontSize="small" />
            <Typography variant="overline">Marketplace</Typography>
          </DashboardCardEyebrow>
          <Typography variant="subtitle1">
            Find attorneys, teams, and services matched to your needs.
          </Typography>
        </Box>
        <DashboardCardEyebrow>
          <Typography variant="button">Explore marketplace</Typography>
          <ArrowForwardRoundedIcon fontSize="small" />
        </DashboardCardEyebrow>
      </MarketplaceBanner>
      {/* A card follows the pointer by a CSS transform, and nothing about a transform stops at an
          edge. Bounding it by the window was not enough: this page owns its scrolling and is
          shorter than the window, so a card could still be hauled below every other card, where
          there is nothing to drop it on and, once the page scrolls under it, no way back. The area
          the page scrolls within is the bound. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToScrollAreaWithPadding(8)]}
        onDragEnd={dropCard}
      >
        <DashboardCardGrid>
          {shownCards.map((cardId, index) => (
            <DraggableDashboardCard
              key={cardId}
              cardId={cardId}
              label={dashboardCardLabels[cardId]}
              position={index + 1}
              total={shownCards.length}
              onMoveTo={moveCard}
            >
              {cardsById[cardId]}
            </DraggableDashboardCard>
          ))}
        </DashboardCardGrid>
      </DndContext>
    </Main>
  );
}

function UpcomingEvent({ event }: Readonly<{ event: CalendarEventResponse }>) {
  if (event.link?.startsWith("/") && !event.link.startsWith("//")) {
    return (
      <DashboardRowLink to={event.link} aria-label={`Open ${event.title}`}>
        <UpcomingEventContent event={event} linked />
      </DashboardRowLink>
    );
  }

  const safeExternalLink = event.link ? getSafeExternalLink(event.link) : undefined;
  if (safeExternalLink) {
    return (
      <DashboardExternalRowLink
        href={safeExternalLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${event.title}`}
      >
        <UpcomingEventContent event={event} linked />
      </DashboardExternalRowLink>
    );
  }

  return <DashboardRow><UpcomingEventContent event={event} /></DashboardRow>;
}

function UpcomingEventContent(props: Readonly<{ event: CalendarEventResponse; linked?: boolean }>) {
  const { event } = props;
  const linked = props.linked ?? false;
  const start = calendarEventStart(event);
  return (
    <>
      <DashboardRowMarker aria-hidden="true" emphasis="quiet">
        <EventDateMonth variant="caption">
          {new Intl.DateTimeFormat(undefined, { month: "short" }).format(start).toUpperCase()}
        </EventDateMonth>
        <EventDateDay variant="h6">
          {new Intl.DateTimeFormat(undefined, { day: "numeric" }).format(start)}
        </EventDateDay>
      </DashboardRowMarker>
      <DashboardRowText>
        <Typography noWrap variant="subtitle2">{event.title}</Typography>
        <Typography color="text.secondary" noWrap variant="caption">{formatEventRange(event)}</Typography>
      </DashboardRowText>
      {linked && <ArrowForwardRoundedIcon color="action" fontSize="small" />}
    </>
  );
}

function DashboardCalendarDay(props: PickerDayProps) {
  const context = useContext(DashboardCalendarContext);
  if (!context) throw new Error("DashboardCalendarDay must be rendered inside DashboardCalendarContext.");

  const day = props.day as Moment;
  const date = day.format("YYYY-MM-DD");
  // A day from the next or previous month is an empty filler cell: MUI draws no number in it, so a
  // dot there reads as belonging to the day beside it. The month's range ends at midnight on the
  // 1st, which is how the 1st's events arrive at all.
  const isFillerCell = Boolean(props.outsideCurrentMonth) && !props.showDaysOutsideCurrentMonth;
  const events = isFillerCell ? [] : context.eventsByDate.get(date) ?? [];
  const selectedEvent = selectDashboardCalendarEvent(events);
  const eventKinds = events.map(getCalendarEventKind);
  const presentation = getDashboardCalendarDayPresentation(
    events.length,
    Boolean(props.today),
    day.isBefore(new AHDate().asMoment, "day"),
  );
  const dotPresentation = getDashboardEventDotPresentation(events.length);
  const eventTypes = [...new Set(eventKinds)].map((kind) => calendarEventKindLabels[kind]).join(", ");
  const formattedDate = day.format("MMMM D, YYYY");
  const ariaLabel = eventKinds.length > 0
    ? `${formattedDate}, ${eventKinds.length} calendar ${eventKinds.length === 1 ? "event" : "events"}: ${eventTypes}`
    : formattedDate;

  const tooltip = events.length > 0 ? (
    <CalendarTooltipContent>
      {events.map((event) => {
        const kind = getCalendarEventKind(event);
        return <span key={`${event.id}:${String(event.start)}`}>{event.title} ({calendarEventKindLabels[kind]})</span>;
      })}
    </CalendarTooltipContent>
  ) : "";

  return (
    <Tooltip arrow disableHoverListener={events.length === 0} title={tooltip}>
      <CalendarDayFrame shadeEntireDay={presentation.shadeEntireDay}>
        <CalendarPickerDay
          {...props}
          aria-label={ariaLabel}
          shadeEntireDay={presentation.shadeEntireDay}
          onClick={(event) => {
            props.onClick?.(event);
            context.openCalendar(date, selectedEvent);
          }}
        />
        {presentation.showEventDots && (
          <CalendarEventDots aria-hidden="true">
            {events.slice(0, dotPresentation.visibleDotCount).map((event) => (
              <CalendarEventDot
                key={`${event.id}:${String(event.start)}`}
                eventKind={getCalendarEventKind(event)}
              />
            ))}
            {dotPresentation.showOverflow && <CalendarEventOverflow>+</CalendarEventOverflow>}
          </CalendarEventDots>
        )}
      </CalendarDayFrame>
    </Tooltip>
  );
}

function createUpcomingEventWindow(): {
  range: CalendarEventRange;
  referenceDate: Date;
} {
  const referenceDate = new Date();
  const rangeStart = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );
  const rangeEnd = new Date(rangeStart);
  rangeEnd.setDate(rangeEnd.getDate() + dashboardUpcomingRangeDays);
  return {
    referenceDate,
    range: {
      from: rangeStart.toISOString(),
      to: rangeEnd.toISOString(),
    },
  };
}

function selectUpcomingEvents(
  events: CalendarEventResponse[],
  referenceDate: Date,
): CalendarEventResponse[] {
  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );
  return events
    .filter((event) => calendarEventStart(event).getTime() >= (event.isAllDay ? today : referenceDate).getTime())
    .sort((left, right) => calendarEventStart(left).getTime() - calendarEventStart(right).getTime());
}

function calendarEventStart(event: Pick<CalendarEventResponse, "isAllDay" | "start">): Date {
  const value = String(event.start);
  return event.isAllDay
    ? new Date(`${value.slice(0, 10)}T00:00:00`)
    : new Date(value);
}

/**
 * The next few events, and the way into the calendar for the rest.
 *
 * Lifted out of the page so every cell of the grid is one component and the order can simply map
 * over them (#5141). It holds no state and asks for nothing: what it draws is decided above.
 */
function UpcomingEventsCard(props: Readonly<{
  isLoading: boolean;
  isError: boolean;
  events: CalendarEventResponse[];
  additionalCount: number;
}>) {
  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <CalendarMonthRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Calendar</Typography>
          </DashboardCardEyebrow>
          <DashboardCardTitle label="Calendar" to={calendarRoute}>
            Upcoming events
          </DashboardCardTitle>
        </Box>
      </DashboardCardHeader>
      {props.isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading upcoming events…</Typography>
        </DashboardCardState>
      )}
      {props.isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">Upcoming events could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!props.isLoading && !props.isError && props.events.length === 0 && (
        <DashboardCardState>
          <Typography variant="body2">No upcoming events in the next year.</Typography>
        </DashboardCardState>
      )}
      {!props.isLoading && !props.isError && props.events.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {props.events.map((event) => (
              <UpcomingEvent key={`${event.id}:${String(event.start)}`} event={event} />
            ))}
          </Stack>
          {props.additionalCount > 0 && (
            <Button component={RouterLink} to={calendarRoute} size="small">
              +{props.additionalCount} more {props.additionalCount === 1 ? "event" : "events"}
            </Button>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}

/** This month at a glance, for the same reason. */
function CalendarMonthCard(props: Readonly<{
  calendarContext: DashboardCalendarContextValue;
  isLoading: boolean;
  isError: boolean;
  onMonthChange: (month: Moment) => void;
}>) {
  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <CalendarMonthRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Calendar</Typography>
          </DashboardCardEyebrow>
          <Typography variant="h5" component="h2">This month</Typography>
        </Box>
      </DashboardCardHeader>
      <DashboardCalendarContext.Provider value={props.calendarContext}>
        <Calendar
          defaultValue={new AHDate().asMoment}
          loading={props.isLoading}
          onMonthChange={(month) => props.onMonthChange(month as Moment)}
          renderLoading={() => <CalendarLoadingState role="status">Loading calendar events…</CalendarLoadingState>}
          slots={{ day: DashboardCalendarDay }}
        />
      </DashboardCalendarContext.Provider>
      {props.isError && (
        <Typography color="error" variant="caption">Dashboard calendar events could not be loaded.</Typography>
      )}
    </DashboardPanelCard>
  );
}
